import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Globe,
  Clock,
  Search,
  Mail,
  MessageSquare,
  Link2,
  AlertTriangle,
  CheckCircle,
  RefreshCw,
  Server,
} from "lucide-react";
import TenantPreferencesService, {
  SystemConfig,
} from "../../_services/TenantPreferencesService";

interface Props {
  onError: (msg: string | null) => void;
  onSuccess: (msg: string | null) => void;
}

const EMPTY: SystemConfig = {
  timeZone: "",
  ilearnPublicUrl: "",
  emailMode: "simple",
  emailAddress: "",
  emailDisplayName: "",
  smsSenderId: "",
  runtime: {
    cacheTimeZone: "",
    cacheUtcOffset: "",
    cacheTzLabel: "",
    bootTimeZone: "",
    bootUtcOffset: "",
    serverUtcNow: "",
  },
};

/** Zones our tenants actually live in — pinned to the top of the picker. */
const POPULAR_ZONES = [
  "Asia/Kolkata",
  "Asia/Dubai",
  "Asia/Riyadh",
  "Asia/Singapore",
  "Europe/London",
  "America/New_York",
];

const IST_FALLBACK = "Asia/Kolkata";

// ---- Intl helpers (results memoized — Intl.DateTimeFormat construction isn't free) ----

const offsetCache = new Map<string, string>();
/** "GMT+04:00"-style offset for an IANA zone, at the given instant. */
function offsetOf(tz: string, at: Date = new Date()): string {
  const cached = offsetCache.get(tz);
  if (cached) return cached;
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      timeZoneName: "longOffset",
    }).formatToParts(at);
    const raw = parts.find((p) => p.type === "timeZoneName")?.value || "";
    const val = raw === "GMT" ? "GMT+00:00" : raw;
    offsetCache.set(tz, val);
    return val;
  } catch {
    return "";
  }
}

const labelCache = new Map<string, string>();
/** Short zone label ("IST", "GST", "GMT+8"). en-IN matches the backend's choice. */
function labelOf(tz: string): string {
  const cached = labelCache.get(tz);
  if (cached) return cached;
  try {
    const parts = new Intl.DateTimeFormat("en-IN", {
      timeZone: tz,
      timeZoneName: "short",
    }).formatToParts(new Date());
    const val = parts.find((p) => p.type === "timeZoneName")?.value || tz;
    labelCache.set(tz, val);
    return val;
  } catch {
    return tz;
  }
}

const dstCache = new Map<string, boolean>();
/** True when the zone observes DST (offset differs between January and July). */
function hasDst(tz: string): boolean {
  const cached = dstCache.get(tz);
  if (cached !== undefined) return cached;
  try {
    const year = new Date().getFullYear();
    const jan = offsetAt(tz, new Date(Date.UTC(year, 0, 1)));
    const jul = offsetAt(tz, new Date(Date.UTC(year, 6, 1)));
    const val = jan !== jul;
    dstCache.set(tz, val);
    return val;
  } catch {
    return false;
  }
}
function offsetAt(tz: string, at: Date): string {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    timeZoneName: "longOffset",
  }).formatToParts(at);
  return parts.find((p) => p.type === "timeZoneName")?.value || "";
}

function allZones(): string[] {
  const intl = Intl as any;
  if (typeof intl.supportedValuesOf === "function") {
    try {
      return intl.supportedValuesOf("timeZone") as string[];
    } catch {
      /* fall through */
    }
  }
  // Very old browsers: at least offer the zones we know tenants use
  return POPULAR_ZONES;
}

const SystemTab: React.FC<Props> = ({ onError, onSuccess }) => {
  const [config, setConfig] = useState<SystemConfig>(EMPTY);
  const [original, setOriginal] = useState<SystemConfig>(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [lastSave, setLastSave] = useState<{ restartRequired: boolean } | null>(null);

  // timezone picker state
  const [pickerOpen, setPickerOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [highlight, setHighlight] = useState(0);
  const pickerRef = useRef<HTMLDivElement | null>(null);
  const searchRef = useRef<HTMLInputElement | null>(null);

  // live clock tick
  const [now, setNow] = useState<Date>(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // close picker on outside click
  useEffect(() => {
    if (!pickerOpen) return;
    const handler = (e: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) {
        setPickerOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [pickerOpen]);

  const load = async () => {
    setLoading(true);
    onError(null);
    try {
      const data = await TenantPreferencesService.getSystemConfig();
      setConfig(data);
      setOriginal(data);
      setLastSave(null);
    } catch (e: any) {
      onError(e?.response?.data?.error?.message || e?.message || "Failed to load system settings");
    } finally {
      setLoading(false);
    }
  };

  const setField = (key: keyof SystemConfig, value: string) => {
    setConfig((prev) => ({ ...prev, [key]: value }));
  };

  const dirty =
    config.timeZone !== original.timeZone ||
    config.ilearnPublicUrl !== original.ilearnPublicUrl ||
    config.emailAddress !== original.emailAddress ||
    config.emailDisplayName !== original.emailDisplayName ||
    config.smsSenderId !== original.smsSenderId;

  const handleSave = async () => {
    setSaving(true);
    onError(null);
    onSuccess(null);
    try {
      const result = await TenantPreferencesService.saveSystemConfig({
        timeZone: config.timeZone,
        ilearnPublicUrl: config.ilearnPublicUrl,
        ...(config.emailMode === "simple" ? { emailAddress: config.emailAddress } : {}),
        emailDisplayName: config.emailDisplayName,
        smsSenderId: config.smsSenderId,
      });
      setLastSave({ restartRequired: result.restartRequired });
      onSuccess(
        result.restartRequired
          ? "Saved. Availability checks follow the new timezone immediately; a nodefork restart completes the switch."
          : "System settings saved.",
      );
      // re-fetch so runtime state (cache zone/offset) reflects the hot reload
      const fresh = await TenantPreferencesService.getSystemConfig();
      setConfig(fresh);
      setOriginal(fresh);
    } catch (e: any) {
      onError(e?.response?.data?.error?.message || e?.message || "Failed to save system settings");
    } finally {
      setSaving(false);
    }
  };

  // ---- timezone picker data ----
  const zones = useMemo(() => allZones(), []);
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase().replace(/\s+/g, "_");
    if (!q) return zones;
    return zones.filter((z) => z.toLowerCase().includes(q) || labelOf(z).toLowerCase().includes(query.trim().toLowerCase()));
  }, [zones, query]);

  /** [groupLabel, zones[]][] — Popular first (only when unfiltered), then by region. */
  const grouped = useMemo(() => {
    const groups: [string, string[]][] = [];
    if (!query.trim()) {
      groups.push(["Popular", POPULAR_ZONES.filter((z) => zones.includes(z))]);
    }
    const byRegion = new Map<string, string[]>();
    for (const z of filtered) {
      const region = z.includes("/") ? z.split("/")[0] : "Other";
      if (!byRegion.has(region)) byRegion.set(region, []);
      byRegion.get(region)!.push(z);
    }
    for (const region of Array.from(byRegion.keys()).sort()) {
      groups.push([region, byRegion.get(region)!]);
    }
    return groups;
  }, [filtered, query, zones]);

  const flatList = useMemo(() => grouped.flatMap(([, zs]) => zs), [grouped]);

  const selectZone = (tz: string) => {
    setField("timeZone", tz);
    setPickerOpen(false);
    setQuery("");
  };

  const onPickerKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, flatList.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const tz = flatList[highlight];
      if (tz) selectZone(tz);
    } else if (e.key === "Escape") {
      setPickerOpen(false);
    }
  };

  useEffect(() => {
    setHighlight(0);
  }, [query, pickerOpen]);

  // ---- derived display values ----
  const effectiveZone = config.timeZone || IST_FALLBACK; // what the picker previews
  const savedIsDefault = !config.timeZone;
  const bootZone = config.runtime.bootTimeZone;
  const restartPending =
    !!config.timeZone && !!bootZone && config.timeZone !== bootZone;

  const clock = (tz: string) => {
    try {
      return new Intl.DateTimeFormat("en-GB", {
        timeZone: tz,
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
      }).format(now);
    } catch {
      return "--:--:--";
    }
  };
  const dateLine = (tz: string) => {
    try {
      return new Intl.DateTimeFormat("en-GB", {
        timeZone: tz,
        weekday: "short",
        day: "2-digit",
        month: "short",
        year: "numeric",
      }).format(now);
    } catch {
      return "";
    }
  };

  if (loading) return <div className="tp-loading">Loading system settings…</div>;

  return (
    <>
      {/* ---------------- Timezone ---------------- */}
      <div className="tp-card">
        <h3>
          <Globe size={16} style={{ marginRight: 8, verticalAlign: "-2px" }} />
          Tenant Timezone
        </h3>
        <p className="tp-card-desc">
          Drives test availability windows, resource validity, scheduling, PDF timestamps, and
          cron jobs for every user of this tenant. Stored in{" "}
          <code>customer_detail_master.JsonConfig.system.timeZone</code>.
        </p>

        <div className="tp-sys-clock-row">
          <div className={`tp-sys-clock ${dirty && config.timeZone !== original.timeZone ? "pending" : "selected"}`}>
            <div className="tp-sys-clock-head">
              <Clock size={14} />
              {config.timeZone !== original.timeZone ? "Selected (unsaved)" : "Configured zone"}
            </div>
            <div className="tp-sys-clock-time">{clock(effectiveZone)}</div>
            <div className="tp-sys-clock-date">{dateLine(effectiveZone)}</div>
            <div className="tp-sys-chiprow">
              <span className="tp-sys-chip">{effectiveZone}</span>
              <span className="tp-sys-chip">{offsetOf(effectiveZone)}</span>
              <span className="tp-sys-chip">{labelOf(effectiveZone)}</span>
              {savedIsDefault && <span className="tp-sys-chip warn">default — nothing saved</span>}
              {hasDst(effectiveZone) && <span className="tp-sys-chip warn">observes DST</span>}
            </div>
          </div>

          <div className="tp-sys-clock server">
            <div className="tp-sys-clock-head">
              <Server size={14} />
              Server is running in
            </div>
            <div className="tp-sys-clock-time">{clock(bootZone || IST_FALLBACK)}</div>
            <div className="tp-sys-clock-date">{dateLine(bootZone || IST_FALLBACK)}</div>
            <div className="tp-sys-chiprow">
              <span className="tp-sys-chip">{bootZone || "TZ not set (IST default)"}</span>
              {config.runtime.bootUtcOffset && (
                <span className="tp-sys-chip">{config.runtime.bootUtcOffset}</span>
              )}
              <span className="tp-sys-chip">
                SQL checks: {config.runtime.cacheTimeZone} ({config.runtime.cacheUtcOffset})
              </span>
            </div>
          </div>
        </div>

        {(restartPending || lastSave?.restartRequired) && (
          <div className="tp-sys-banner warn">
            <AlertTriangle size={16} />
            <div>
              <strong>Restart required to fully apply.</strong> SQL availability checks already
              follow the saved zone (hot-reloaded on save), but the process timezone and database
              session offset are fixed at startup. Ask ops to restart nodefork for this tenant to
              complete the switch.
            </div>
          </div>
        )}
        {!restartPending && !savedIsDefault && config.timeZone === original.timeZone && (
          <div className="tp-sys-banner ok">
            <CheckCircle size={16} />
            <div>
              Timezone is fully active — saved zone and running server agree.
            </div>
          </div>
        )}
        {hasDst(effectiveZone) && (
          <div className="tp-sys-banner warn">
            <AlertTriangle size={16} />
            <div>
              <strong>{effectiveZone} observes daylight saving.</strong> The platform caches a
              fixed UTC offset at startup, so clocks drift by an hour at each DST transition until
              the next restart. Check with engineering before putting a tenant on a DST zone.
            </div>
          </div>
        )}

        <div className="tp-sys-picker" ref={pickerRef}>
          <label className="tp-field-label" htmlFor="tp-sys-tz-search">Change timezone</label>
          <div className="tp-sys-picker-input">
            <Search size={14} className="tp-sys-picker-icon" />
            <input
              id="tp-sys-tz-search"
              ref={searchRef}
              className="tp-field-input"
              type="text"
              placeholder={config.timeZone || "Search zones — e.g. Dubai, Kolkata, London"}
              value={query}
              onFocus={() => setPickerOpen(true)}
              onChange={(e) => {
                setQuery(e.target.value);
                setPickerOpen(true);
              }}
              onKeyDown={onPickerKeyDown}
              autoComplete="off"
            />
          </div>
          {pickerOpen && (
            <div className="tp-sys-picker-list" role="listbox">
              {flatList.length === 0 && (
                <div className="tp-sys-picker-empty">No zone matches “{query}”</div>
              )}
              {grouped.map(([groupLabel, zs]) =>
                zs.length === 0 ? null : (
                  <div key={groupLabel}>
                    <div className="tp-sys-picker-group">{groupLabel}</div>
                    {zs.map((z) => {
                      const idx = flatList.indexOf(z);
                      return (
                        <button
                          key={`${groupLabel}:${z}`}
                          type="button"
                          role="option"
                          aria-selected={z === config.timeZone}
                          className={`tp-sys-picker-item ${idx === highlight ? "hl" : ""} ${z === config.timeZone ? "current" : ""}`}
                          onMouseEnter={() => setHighlight(idx)}
                          onClick={() => selectZone(z)}
                        >
                          <span className="tp-sys-picker-zone">{z.replace(/_/g, " ")}</span>
                          <span className="tp-sys-picker-offset">{offsetOf(z)}</span>
                        </button>
                      );
                    })}
                  </div>
                ),
              )}
            </div>
          )}
          <div className="tp-slot-hint">
            Key: <code>system.timeZone</code> — IANA name. Saving also updates the legacy{" "}
            <code>system.appTimeZone</code> so older builds stay consistent.
          </div>
        </div>
      </div>

      {/* ---------------- Email & SMS sender ---------------- */}
      <div className="tp-card">
        <h3>
          <Mail size={16} style={{ marginRight: 8, verticalAlign: "-2px" }} />
          Default Email &amp; SMS Sender
        </h3>
        <p className="tp-card-desc">
          Used as the “from” identity for every notification this tenant sends (email, SMS,
          WhatsApp templates). Columns: <code>Email</code>, <code>EmailDisplayName</code>,{" "}
          <code>SmsSenderId</code>.
        </p>

        {config.emailMode === "advanced" ? (
          <div className="tp-sys-banner info">
            <AlertTriangle size={16} />
            <div>
              This tenant uses <strong>advanced per-audience email routing</strong> (JSON in the{" "}
              <code>Email</code> column) — the from-address must be edited in the database. Display
              name and SMS sender below are still editable.
            </div>
          </div>
        ) : (
          <div className="tp-dimensions" style={{ maxWidth: 720 }}>
            <div>
              <label className="tp-field-label" htmlFor="tp-sys-email">From Address</label>
              <input
                id="tp-sys-email"
                className="tp-field-input"
                type="text"
                placeholder="e.g. noreply@yourschool.ae"
                value={config.emailAddress}
                onChange={(e) => setField("emailAddress", e.target.value)}
              />
              <div className="tp-slot-hint">Blank falls back to <code>noreply@conceptwaves.com</code>.</div>
            </div>
          </div>
        )}

        <div className="tp-dimensions" style={{ maxWidth: 720, marginTop: 12 }}>
          <div>
            <label className="tp-field-label" htmlFor="tp-sys-email-name">From Display Name</label>
            <input
              id="tp-sys-email-name"
              className="tp-field-input"
              type="text"
              placeholder="e.g. ATP STEM Academy"
              value={config.emailDisplayName}
              onChange={(e) => setField("emailDisplayName", e.target.value)}
            />
          </div>
          <div>
            <label className="tp-field-label" htmlFor="tp-sys-sms">
              <MessageSquare size={13} style={{ marginRight: 6, verticalAlign: "-2px" }} />
              SMS Sender ID
            </label>
            <input
              id="tp-sys-sms"
              className="tp-field-input"
              type="text"
              placeholder="e.g. ATPSTM"
              value={config.smsSenderId}
              onChange={(e) => setField("smsSenderId", e.target.value)}
            />
            <div className="tp-slot-hint">Registered sender/header ID with your SMS provider.</div>
          </div>
        </div>

        {(config.emailDisplayName || config.emailAddress) && config.emailMode === "simple" && (
          <div className="tp-sys-from-preview">
            Recipients will see:&nbsp;
            <strong>
              {config.emailDisplayName ? `${config.emailDisplayName} ` : ""}
              &lt;{config.emailAddress || "noreply@conceptwaves.com"}&gt;
            </strong>
          </div>
        )}
      </div>

      {/* ---------------- iLearn public URL ---------------- */}
      <div className="tp-card">
        <h3>
          <Link2 size={16} style={{ marginRight: 8, verticalAlign: "-2px" }} />
          iLearn Public URL
        </h3>
        <p className="tp-card-desc">
          The student portal’s public address — used when the backend must send users back to
          iLearn from an external flow (payment-gateway returns, shared links).
        </p>
        <div className="tp-dimensions" style={{ maxWidth: 720 }}>
          <div>
            <label className="tp-field-label" htmlFor="tp-sys-ilearn-url">URL</label>
            <input
              id="tp-sys-ilearn-url"
              className="tp-field-input"
              type="text"
              placeholder="https://<tenant>.edusquares.com"
              value={config.ilearnPublicUrl}
              onChange={(e) => setField("ilearnPublicUrl", e.target.value)}
            />
            <div className="tp-slot-hint">Key: <code>system.ilearnPublicUrl</code> — no trailing slash.</div>
          </div>
        </div>
      </div>

      <div className="tp-slot-actions" style={{ justifyContent: "flex-end", marginTop: 4 }}>
        <button
          type="button"
          className="tp-btn tp-btn-secondary"
          onClick={() => void load()}
          disabled={saving}
        >
          <RefreshCw size={14} style={{ marginRight: 6, verticalAlign: "-2px" }} />
          Reload
        </button>
        <button
          type="button"
          className="tp-btn tp-btn-primary"
          onClick={() => void handleSave()}
          disabled={!dirty || saving}
        >
          {saving ? "Saving…" : "Save System Settings"}
        </button>
      </div>
    </>
  );
};

export default SystemTab;
